import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatDetail } from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";

/**
 * 2026-10-19 (user request anti double-counting retensi): dialog FAT untuk
 * menautkan klaim retensi legacy ke invoice sumber retensinya. Pilihan user:
 *   - 1b: klaim baru punya `retention_source_entry_id`; legacy tanpa tautan
 *     tetap mengurangi total "Belum Diklaim" (bukan bucket aging), ditandai
 *     "Perlu Verifikasi" sampai FAT memilih sumbernya di sini.
 *   - 4: tautan dicegah menguras lebih dari sisa retensi sumber (backend
 *     menolak over-claim 400); perubahan tersimpan di Audit Log.
 */
export default function LinkRetentionSourceDialog({ open, onOpenChange, claim, onLinked }) {
  const [cand, setCand] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !claim?.entry_id) return;
    setCand(null); setSel(null);
    api.get(`/active-projects/retention-claim-candidates/${claim.entry_id}`)
      .then(({ data }) => { setCand(data); setSel(data?.current_source_entry_id || null); })
      .catch((err) => { toast.error(formatDetail(err.response?.data?.detail) || "Gagal memuat kandidat"); onOpenChange?.(false); });
  }, [open, claim?.entry_id, onOpenChange]);

  const save = async () => {
    if (!claim?.project_id || !claim?.entry_id) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/active-projects/${claim.project_id}/income/${claim.entry_id}/link-retention-source`, { source_entry_id: sel });
      toast.success(data?.source_label ? `Tertaut ke ${data.source_label}` : "Tautan sumber diperbarui");
      onLinked?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menautkan");
    } finally { setBusy(false); }
  };

  const claimAmount = Number(claim?.gross_invoice_amount || claim?.outstanding || 0);
  const sortedCands = useMemo(() => (cand?.candidates || []).slice().sort((a, b) => b.retention_held_available - a.retention_held_available), [cand]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="link-retention-source-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Tautkan Invoice Sumber Retensi</DialogTitle>
          <DialogDescription className="text-xs">
            Klaim Retensi · <span className="font-mono">{claim?.invoice_number || claim?.entry_id?.slice(0, 8)}</span>
            {" · "}Nilai klaim <b>{formatIDR(claimAmount)}</b>. Pilih invoice sumber retensi (di proyek induk + semua WO/VO anak) yang masih punya retensi tertahan. Pengurangan dicatat di <b>bucket aging sumbernya</b> secara presisi.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-600 bg-amber-50 border border-amber-200 px-3 py-2">
            <b>Aturan:</b> Satu klaim hanya boleh menguras satu invoice sumber. Nilai klaim tidak boleh melebihi sisa retensi tertahan pada sumber tersebut. Perubahan tautan disimpan di Audit Log — tidak mengubah data historis invoice.
          </div>
          {!cand ? (
            <div className="text-xs text-slate-500 italic">Memuat kandidat…</div>
          ) : sortedCands.length === 0 ? (
            <div className="text-xs text-rose-700 italic">Tidak ada invoice sumber retensi di proyek induk + anak. Klaim mungkin perlu dibatalkan atau proyek lain yang benar.</div>
          ) : (
            <div className="border border-slate-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-2 py-2 text-left w-10">Pilih</th>
                    <th className="px-3 py-2 text-left">Proyek · Invoice</th>
                    <th className="px-3 py-2 text-left">Tgl Invoice</th>
                    <th className="px-3 py-2 text-right">Retensi Tersisa</th>
                    <th className="px-3 py-2 text-left">Jatuh Tempo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={`border-t border-slate-100 ${sel === null ? "bg-sky-50" : ""} cursor-pointer`} onClick={() => setSel(null)} data-testid="link-source-none">
                    <td className="px-2 py-2 text-center"><input type="radio" checked={sel === null} onChange={() => setSel(null)} /></td>
                    <td className="px-3 py-2 text-slate-700 italic" colSpan={4}>— Tidak ada / belum ditautkan (klaim tetap "Perlu Verifikasi")</td>
                  </tr>
                  {sortedCands.map((c) => {
                    const insufficient = claimAmount > c.retention_held_available + 0.5;
                    return (
                      <tr key={c.entry_id}
                        className={`border-t border-slate-100 ${sel === c.entry_id ? "bg-emerald-50" : ""} ${insufficient ? "opacity-60" : "cursor-pointer hover:bg-slate-50"}`}
                        onClick={() => { if (!insufficient) setSel(c.entry_id); }}
                        data-testid={`link-source-${c.entry_id}`}>
                        <td className="px-2 py-2 text-center"><input type="radio" checked={sel === c.entry_id} disabled={insufficient} onChange={() => setSel(c.entry_id)} /></td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-800">{c.project_name}</div>
                          <div className="font-mono text-[10px] text-slate-500">{c.invoice_number || c.entry_id.slice(0, 10)}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{c.invoice_date ? fmtDate(c.invoice_date) : "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <div className="font-semibold text-emerald-700">{formatIDR(c.retention_held_available)}</div>
                          {insufficient && <div className="text-[9px] text-rose-600">kurang dari klaim</div>}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{c.retention_due_date ? fmtDate(c.retention_due_date) : <span className="text-slate-400 italic">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="link-source-cancel">Batal</Button>
            <Button type="button" onClick={save} disabled={busy} data-testid="link-source-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? "Menyimpan…" : "Simpan Tautan"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
